import { FC, ReactNode, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { supabase } from '../lib/supabase';
import { useAppSelector } from '../store/store';
import { ambiente } from '../nucleo/ambiente';

// Estado de consentimento do usuário logado (LGPD).
//
// Fica num contexto porque duas partes precisam do MESMO dado: o gate (RequireConsent), que decide
// se libera o app, e a tela /consentimento, que coleta. Sem isso as duas chamariam a função
// separadamente e a tela poderia renderizar com um estado mais velho que o do gate.

export interface PendingDoc {
  slug: string;
  version: number;
  title: string;
}

export interface ConsentState {
  blocked: boolean;
  reviewStatus: 'ok' | 'menor_em_revisao';
  needsBirthDate: boolean;
  pendingDocs: PendingDoc[];
  satisfied: boolean;
  comunicacoes: boolean;
  pesquisa: string | null;
}

interface ConsentContextValue {
  state: ConsentState | null;
  loading: boolean;
  /** Erro de rede/servidor ao consultar. Diferente de "não consentiu". */
  unavailable: boolean;
  refresh: () => Promise<void>;
  apply: (next: ConsentState) => void;
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

// ─── Consentimento coletado no cadastro, à espera de sessão ──────────────────────────────────
//
// O cadastro por e-mail já pergunta idade e aceite, mas só existe sessão DEPOIS do código — e
// nesse instante o PublicOnly redireciona e desmonta a tela. Enviar dali era uma corrida: a
// chamada saía no meio do desmonte e falhava calada, e a pessoa reencontrava o mesmo formulário
// no gate, já tendo preenchido tudo.
//
// Guardar e deixar o envio para o provider elimina a corrida: ele está montado, estável, e é
// exatamente quem precisa do resultado.
const CHAVE_PENDENTE = 'maestra_consentimento_pendente';

export interface ConsentimentoPendente {
  email: string;
  birthDate: string;
  aceita: boolean;
  comunicacoes: boolean;
}

export const guardarConsentimentoPendente = (p: ConsentimentoPendente): void => {
  ambiente().sessao.gravar(CHAVE_PENDENTE, JSON.stringify(p));
};

const lerConsentimentoPendente = (): ConsentimentoPendente | null => {
  try {
    const cru = ambiente().sessao.ler(CHAVE_PENDENTE);
    return cru ? (JSON.parse(cru) as ConsentimentoPendente) : null;
  } catch {
    return null;
  }
};

const limparConsentimentoPendente = (): void => {
  ambiente().sessao.apagar(CHAVE_PENDENTE);
};

/**
 * O estado do consentimento de UM usuário, e o envio do que ficou pendente do cadastro.
 *
 * ⚠️ CHAME-O UMA VEZ POR ÁRVORE, pelo provider, e leia-o pelo `useConsent`. Cada chamada é uma
 * consulta e um estado SEPARADOS: duas delas na mesma árvore são duas verdades sobre a mesma
 * pessoa, e o `apply` de uma não chega à outra. O app nativo já se partiu assim (ver o
 * `ProvedorDoConsentimento`, logo abaixo).
 *
 * Está extraído do provider porque os dois lados descobrem o usuário de maneiras diferentes: a
 * web tem o slice de auth, e o app nativo tem a sessão do Supabase — o login social nunca passa
 * pelo slice. Reescrever a lógica do outro lado seria reescrever a regra que decide quem entra
 * sem ter declarado idade.
 */
export const useEstadoDoConsentimento = (user: { id: string; email?: string } | null) => {
  // ⚠️ AS DEPENDÊNCIAS SÃO O `id` E O `email`, NUNCA O OBJETO `user`.
  //
  // O provider da web passa a referência do store, que é estável. O app nativo não tem store
  // para isto — a sessão vem do Supabase — e monta `{ id, email }` na chamada, um objeto NOVO a
  // cada render. Com o objeto na lista do `useCallback`, o `refresh` nascia diferente a cada
  // render, o `useEffect` abaixo disparava outra vez, a resposta trocava o estado, e o estado
  // trazia outro render: o portão e a tela de consentimento ficavam a consultar a edge em
  // círculo até o React desistir com "Maximum update depth exceeded".
  //
  // Escalares cortam o laço na raiz, e sem exigir que cada chamador se lembre de memoizar.
  const id = user?.id ?? null;
  const email = user?.email;

  const [state, setState] = useState<ConsentState | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);

  // Quem já foi respondido. Uma REVERIFICAÇÃO da mesma pessoa não pode voltar para `loading`:
  // o `RequireConsent` troca a árvore inteira por um spinner enquanto isso, e desmontar a árvore
  // apaga a página que a pessoa estava preenchendo. Trocar de conta, sim, precisa apagar — senão
  // quem entra herda por um instante o veredito de quem saiu, e este gate é o da idade.
  const idAtendido = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) {
      idAtendido.current = null;
      setState(null);
      setLoading(false);
      return;
    }
    if (idAtendido.current !== id) setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('account-consent', {
        body: { action: 'state' },
      });
      if (error || !data || data.error) throw error || new Error(data?.error || 'Sem resposta');

      let estado = data as ConsentState;

      // Acabou de se cadastrar e já respondeu tudo no formulário: envia agora, e a pessoa nunca vê
      // a tela de consentimento. O e-mail precisa bater — uma aba reaproveitada por outra conta
      // não pode herdar a declaração de idade de ninguém.
      const pendente = lerConsentimentoPendente();
      if (
        pendente && pendente.aceita && !estado.satisfied && !estado.blocked &&
        email && pendente.email.toLowerCase() === email.toLowerCase()
      ) {
        limparConsentimentoPendente();
        const { data: enviado } = await supabase.functions.invoke('account-consent', {
          body: {
            action: 'submit',
            birthDate: pendente.birthDate,
            aceitaTermos: true,
            aceitaPolitica: true,
            aceitaComunicacoes: pendente.comunicacoes,
          },
        });
        // Se o envio falhar, fica o estado original e o gate mostra a tela — o pior caso volta a
        // ser o de hoje, nunca deixar passar sem registro.
        if (enviado && !enviado.error) estado = enviado as ConsentState;
      }

      setState(estado);
      setUnavailable(false);
    } catch (e) {
      // Não derruba o app. Ver o comentário do RequireConsent em App.tsx: trancar todo mundo do
      // lado de fora por causa de uma indisponibilidade transitória é pior que o risco que o gate
      // cobre — e a coleta volta a ser exigida na próxima verificação bem-sucedida.
      console.warn('[useConsent] estado indisponível:', e);
      setUnavailable(true);
      setState(null);
    } finally {
      idAtendido.current = id;
      setLoading(false);
    }
  }, [id, email]);

  useEffect(() => { refresh(); }, [refresh]);

  // Aplica a resposta que o próprio submit já devolveu, sem uma segunda ida ao servidor.
  const apply = useCallback((next: ConsentState) => {
    setState(next);
    setUnavailable(false);
  }, []);

  return useMemo(
    () => ({ state, loading, unavailable, refresh, apply }),
    [state, loading, unavailable, refresh, apply]
  );
};

/**
 * O provider que recebe o usuário de QUEM O CHAMA — e é o que o app nativo usa.
 *
 * ⚠️ UM ESTADO SÓ, E É A REGRA INTEIRA. O gate e a tela de coleta precisam do MESMO objeto:
 * quem envia o aceite chama `apply`, e é por `apply` que o gate fica sabendo que já pode
 * destrancar. Duas cópias do hook são duas verdades, e a mais velha ganha — ela é quem manda na
 * rota.
 *
 * O app nativo já pagou por isso: o gate e a tela chamavam `useEstadoDoConsentimento` cada um
 * por si. Quem entrava por Google ou Apple preenchia a data, marcava os aceites, tocava em
 * Continuar — o servidor registrava tudo e devolvia `satisfied: true` — e a tela seguia para as
 * boas-vindas. Só que a cópia do gate continuava a dizer `satisfied: false`, e na primeira
 * mudança de rota ele devolvia a pessoa ao consentimento. A segunda tela vinha sem o campo de
 * data (o servidor já a tinha guardado) e com as caixas outra vez vazias: parecia que o aceite
 * não fora registrado, e tinha sido.
 *
 * O provider da web não serve ao app: ele lê `s.auth.user`, e a sessão do app vem do Supabase
 * direto — o login social nunca passa pelo slice de auth, então um provider keyed ali nunca
 * dispararia.
 */
export const ProvedorDoConsentimento: FC<{
  usuario: { id: string; email?: string } | null;
  children: ReactNode;
}> = ({ usuario, children }) => {
  const value = useEstadoDoConsentimento(usuario);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const ConsentProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const user = useAppSelector((s) => s.auth.user);

  return <ProvedorDoConsentimento usuario={user ?? null}>{children}</ProvedorDoConsentimento>;
};

export const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent precisa estar dentro de <ConsentProvider>');
  return ctx;
};
